import { z } from "zod/v4";

import {
  ANCHOR_KEY_PATTERN,
  ComponentPlacementLocationList,
} from "../../dtos/components";
import { InsertTypeList } from "../../dtos/onsite-edits";
import { JsInjectionModeDtoSchema } from "../../dtos/onsite-injections";
import { ModificationSourceList } from "../../dtos/modification-source";

import {
  OnsiteUpsellDiscountSchema,
  OnsiteUpsellRuleAuthoringSchema,
  refineOnsiteUpsellDiscount,
  refineOnsiteUpsellPlacement,
  refineOnsiteUpsellRules,
  refineOnsiteUpsellTemplate,
} from "./onsite-upsell-rules";

export const ModInsertTypeSchema = z.enum([...InsertTypeList, "replace"]);
export type ModInsertType = z.infer<typeof ModInsertTypeSchema>;

// ── Base shared fields ────────────────────────────────────────────────
// The `.describe()` text on each field is forwarded to the AI as part of the
// tool's JSON Schema and is how the AI learns the meaning of each property.

const SharedBaseModSchema = z.object({
  id: z
    .string()
    .optional()
    .describe(
      "ID of the mod. Omit for new mods — one will be generated. Include to update an existing mod."
    ),
  type: z.string().describe("Discriminator identifying the kind of mod."),
  description: z
    .string()
    .optional()
    .describe("Short human-readable title of the mod shown in the UI."),
});

export type SharedBaseMod = z.infer<typeof SharedBaseModSchema>;

export const SharedTextModSchema = SharedBaseModSchema.extend({
  type: z.literal("text"),
  editMode: z
    .enum(["text", "html"])
    .describe(
      "`text` = plain-text replacement. `html` = HTML replacement (prefer a `genericComponent` mod instead of this mode)."
    ),
  selector: z
    .string()
    .describe(
      "CSS selector targeting the element whose text content is being modified."
    ),
  find: z
    .string()
    .default("")
    .describe(
      "Substring to find within the element's text. Omit or use an empty string to replace the entire text content."
    ),
  replace: z
    .string()
    .describe(
      "Replacement text. When `find` is non-empty, only the matching substring is replaced; otherwise the full content is replaced."
    ),
  html: z
    .string()
    .describe(
      "HTML to inject when `editMode` is `html`. Empty string when `editMode` is `text`."
    ),
  remove: z
    .boolean()
    .default(false)
    .describe(
      "When `true`, the element is removed from the page and other fields are ignored. Omit or use `false` to modify the element instead."
    ),
  insertType: ModInsertTypeSchema.default("replace").describe(
    "How the `replace` value is inserted relative to the target. Uses the same values as `Element.insertAdjacentHTML()`: `beforebegin` (sibling before), `afterbegin` (first child), `beforeend` (last child), `afterend` (sibling after). Plus `replace` to swap the target element's content. Omit to use `replace`."
  ),
});

export type SharedTextMod = z.infer<typeof SharedTextModSchema>;
export type TextEditMode = SharedTextMod["editMode"];

export const SharedImageModSchema = SharedBaseModSchema.extend({
  type: z.literal("image"),
  editMode: z
    .enum(["image", "html"])
    .describe(
      "`image` = replace the `src` of the matched IMG element(s). `html` = replace with arbitrary HTML (prefer a `genericComponent` mod instead)."
    ),
  selector: z.string().describe("CSS selector targeting the IMG element(s)."),
  remove: z
    .boolean()
    .default(false)
    .describe(
      "When `true`, the matched IMG element(s) are removed from the page and other fields are ignored. Omit or use `false` to modify the element instead."
    ),
  src: z
    .string()
    .describe(
      "New image URL to set on the matched IMG element(s). Empty string when `editMode` is `html`."
    ),
  html: z
    .string()
    .describe(
      "HTML to inject when `editMode` is `html`. Empty string when `editMode` is `image`."
    ),
  imageSearchUrl: z
    .string()
    .nullable()
    .default(null)
    .describe(
      "When set, replaces the `src` on every IMG element on the page whose current `src` matches this value (ignores `selector`). Omit or use `null` for a single-image replacement driven by `selector`."
    ),
});

export type SharedImageMod = z.infer<typeof SharedImageModSchema>;
export type ImageEditMode = SharedImageMod["editMode"];

export const SharedLayoutModSchema = SharedBaseModSchema.extend({
  type: z.literal("layout"),
  selector: z
    .string()
    .describe("CSS selector targeting the Layout element(s)."),
  html: z.string().describe("HTML to inject."),
  remove: z
    .boolean()
    .default(false)
    .describe(
      "When `true`, the matched Layout element(s) are removed from the page and other fields are ignored. Omit or use `false` to modify the element instead."
    ),
  insertType: ModInsertTypeSchema.default("replace").describe(
    "How the `replace` value is inserted relative to the target. Uses the same values as `Element.insertAdjacentHTML()`: `beforebegin` (sibling before), `afterbegin` (first child), `beforeend` (last child), `afterend` (sibling after). Plus `replace` to swap the target element's content. Omit to use `replace`."
  ),
});

export type SharedLayoutMod = z.infer<typeof SharedLayoutModSchema>;

export const SharedInjectionModSchema = SharedBaseModSchema.extend({
  type: z.literal("injection"),
  js: z
    .string()
    .default("")
    .describe(
      "JavaScript executed on the page. Empty string means no JS injection."
    ),
  css: z
    .string()
    .default("")
    .describe(
      "CSS injected globally on the page. Empty string means no CSS injection."
    ),
  jsInjectionMode: JsInjectionModeDtoSchema.default({
    type: "immediately",
  }).describe(
    "Controls when the JavaScript is executed. Default `{ type: 'immediately' }`. Use `{ type: 'onWindowLoad' }` to wait for load, or `{ type: 'timeout', timeout: <ms> }` for a delay."
  ),
});

export type SharedInjectionMod = z.infer<typeof SharedInjectionModSchema>;

export const SharedBasicModSchema = z.discriminatedUnion("type", [
  SharedTextModSchema,
  SharedImageModSchema,
  SharedLayoutModSchema,
  SharedInjectionModSchema,
]);

export type SharedBasicMod = z.infer<typeof SharedBasicModSchema>;

const SharedBaseComponentSchema = SharedBaseModSchema.extend({
  selector: z
    .string()
    .describe(
      "CSS selector for the anchor element the component is positioned against."
    ),
  insertType: ModInsertTypeSchema.default("replace").describe(
    "Placement relative to the anchor element. Uses the same values as `Element.insertAdjacentHTML()`: `beforebegin` (sibling before), `afterbegin` (first child), `beforeend` (last child), `afterend` (sibling after). Plus `replace` to swap the anchor element itself. Omit to use `replace`."
  ),
});

export const SharedTextComponentSchema = SharedBaseComponentSchema.extend({
  type: z.literal("textComponent"),
  content: z
    .string()
    .describe("Rich text or HTML content rendered inside the component."),
  lineHeight: z.string().describe('CSS line-height (e.g. "1.4").'),
  letterSpacing: z
    .string()
    .describe('CSS letter-spacing (e.g. "0.02em"). Empty string for none.'),
  padding: z
    .string()
    .describe(
      'CSS padding shorthand (e.g. "8px 12px"). Empty string for none.'
    ),
  margin: z
    .string()
    .describe(
      'CSS margin shorthand (e.g. "0 0 8px 0"). Empty string for none.'
    ),
});

export type SharedTextComponent = z.infer<typeof SharedTextComponentSchema>;

export const SharedImageComponentSchema = SharedBaseComponentSchema.extend({
  type: z.literal("imageComponent"),
  src: z.string().describe("Image URL."),
  alt: z.string().describe("Accessible alt text for the image."),
  width: z.string().describe('CSS width (e.g. "100%" or "320px").'),
  maxHeight: z.string().describe('CSS max-height (e.g. "240px" or "none").'),
  scaling: z
    .enum(["contain", "cover", "fill", "none", "scale-down"])
    .describe("CSS object-fit value controlling how the image fills its box."),
  objectPosition: z
    .enum([
      "center",
      "top",
      "bottom",
      "left",
      "right",
      "top left",
      "top right",
      "bottom left",
      "bottom right",
    ])
    .describe(
      "CSS object-position value controlling alignment within the box."
    ),
});

export type SharedImageComponent = z.infer<typeof SharedImageComponentSchema>;
export type ImageScaling = SharedImageComponent["scaling"];
export type ImagePosition = SharedImageComponent["objectPosition"];

export const SharedBannerComponentSchema = SharedBaseComponentSchema.extend({
  type: z.literal("bannerComponent"),
  content: z
    .string()
    .describe("Rich text or HTML content rendered inside the banner."),
  iconSrc: z
    .string()
    .optional()
    .describe("Optional icon image URL. Omit for a text-only banner."),
  iconPosition: z
    .enum(["left", "right"])
    .describe("Which side of the content the icon is placed on."),
  backgroundColor: z
    .string()
    .describe("Banner background color as a CSS color (e.g. hex)."),
  strokeColor: z.string().describe("Border color as a CSS color."),
  strokeWidth: z
    .string()
    .describe('Border width (e.g. "1px"). Empty string for no border.'),
  cornerRadius: z
    .string()
    .describe('Border radius (e.g. "8px"). Empty string for square corners.'),
  padding: z.string().describe('CSS padding shorthand (e.g. "12px 16px").'),
  margin: z.string().describe('CSS margin shorthand (e.g. "0 0 8px 0").'),
  lineHeight: z.string().describe('CSS line-height (e.g. "1.4").'),
  letterSpacing: z
    .string()
    .describe('CSS letter-spacing (e.g. "0.02em"). Empty string for none.'),
});

export type SharedBannerComponent = z.infer<typeof SharedBannerComponentSchema>;
export type IconPosition = SharedBannerComponent["iconPosition"];

export const SharedGenericComponentSchema = SharedBaseComponentSchema.extend({
  type: z.literal("genericComponent"),
  definition: z
    .string()
    .describe(
      "An `<ig-component>` web-component tag with attributes (prefixed by type — e.g. `rich:`, `color:`, `number:`, `image:`) whose values are substituted into the template in the element's inner HTML via `{{placeholder}}` tokens."
    ),
});

export type SharedGenericComponent = z.infer<
  typeof SharedGenericComponentSchema
>;

// ── Onsite upsell mod ─────────────────────────────────────────────────
// AI-slim, flat shape authored via `update_mods`; the FE handler expands `rules`
// to canonical `OnsiteUpsellConfig` nodes on the way to a persisted `ComponentDto`.
export const SharedOnsiteUpsellComponentSchema = SharedBaseModSchema.extend({
  type: z.literal("onsiteUpsell"),
  location: z
    .enum(ComponentPlacementLocationList)
    .describe(
      "Which surface the upsell renders on — `pdp` (product page) or `cart`."
    ),
  anchorKey: z
    .string()
    .regex(ANCHOR_KEY_PATTERN)
    .optional()
    .describe(
      "Anchor-mode placement: the key the merchant gave an Intelligems Content theme app block. The upsell renders inside every block on the page carrying this key, wherever the merchant put them. Mutually exclusive with `selector`/`insertType` — set exactly one mode."
    ),
  selector: z
    .string()
    .optional()
    .describe(
      'Selector-mode placement: CSS selector for the element the upsell is inserted next to — e.g. `form[action*="/cart/add"]` on a PDP, or `.cart__footer` in the cart. Mutually exclusive with `anchorKey`.'
    ),
  insertType: z
    .enum(InsertTypeList)
    .optional()
    .describe(
      "Selector-mode placement: where the upsell is placed relative to the selector element: `beforebegin` (sibling before), `afterbegin` (first child), `beforeend` (last child), or `afterend` (sibling after). Required with `selector`, unused with `anchorKey`."
    ),
  maxProducts: z
    .number()
    .int()
    .positive()
    .describe(
      "Maximum number of products the upsell renders. A positive integer (e.g. 3)."
    ),
  excludeOutOfStock: z
    .boolean()
    .default(true)
    .describe(
      "Exclude products whose variants are all out of stock. Defaults to true."
    ),
  excludeItemsInCart: z
    .boolean()
    .default(true)
    .describe(
      "Exclude products already in the shopper's cart. Defaults to true."
    ),
  discount: OnsiteUpsellDiscountSchema.nullable()
    .default(null)
    .describe(
      "Discount applied to products added from this upsell — use `null` for no discount. See the sub-fields for how the type, amount, and comparison-price display are set."
    ),
  rules: z
    .array(OnsiteUpsellRuleAuthoringSchema)
    .describe(
      "Ordered rules. For each shopper the FIRST rule whose `conditions` all match is used; a rule with no conditions is always-on, so put it last. Each rule is `{ conditions, products }` — IF the conditions match, THEN show those products."
    ),
  template: z
    .string()
    .describe(
      "The full upsell HTML. The repeating per-product card MUST be inside a `{{#each products}} … {{/each}}` block and must include ALL of these slots — never drop one, even when editing an existing template, because each hides itself when it has no value (so keeping it is always safe) while removing it silently deletes that element: `{{product.image}}`, `{{product.url}}`, `{{product.title}}`, `{{product.price}}`, `{{product.compareAtPrice}}`, `{{variant-select}}`, `{{atc}}`. These are the only allowed slots. No `<script>` tags or inline JavaScript."
    ),
  // The Agentic Logic binding, exposed at the AI boundary so Viv can bind a
  // rule the merchant chose (or one it just created) the same way the panel's
  // rule picker does. Optional with NO default on purpose: an omitted field
  // must not read as "detach" — `fromAiFormat` keeps the stored binding — so
  // an edit to the template or discount can't silently unbind the component.
  // The placement id is deliberately NOT here: the server stamps it on save.
  agenticLogicRuleId: z
    .uuid()
    .nullable()
    .optional()
    .describe(
      "The Agentic Logic rule that selects this upsell's products. ONLY for a store whose `context.agenticLogicEnabled` is `true`, where every upsell mod MUST carry one — the rule replaces the legacy `rules`, `maxProducts` and exclusion fields, which the engine ignores. Use an `id` returned by `list_agentic_logic_rules` (never invent one). Omit the field to keep the rule an existing mod already has; `null` detaches it. Never set it for a store without Agentic Logic."
    ),
});

export type SharedOnsiteUpsellComponent = z.infer<
  typeof SharedOnsiteUpsellComponentSchema
>;

export const SharedComponentModSchema = z.discriminatedUnion("type", [
  SharedTextComponentSchema,
  SharedImageComponentSchema,
  SharedBannerComponentSchema,
  SharedGenericComponentSchema,
  SharedOnsiteUpsellComponentSchema,
]);

export type SharedComponentMod = z.infer<typeof SharedComponentModSchema>;
export type ComponentType = SharedComponentMod["type"];

export const SharedEmbeddedModSchema = z.discriminatedUnion("type", [
  ...SharedBasicModSchema.options,
  ...SharedComponentModSchema.options,
]);

export type SharedEmbeddedMod = z.infer<typeof SharedEmbeddedModSchema>;

// Domain-validated form for standalone validation (tool boundary + FE handler).
// Zod v4 can't union a refined schema, so the plain object joins the mod union
// while this variant adds the cross-field checks.
export const OnsiteUpsellComponentSchema =
  SharedOnsiteUpsellComponentSchema.superRefine((mod, ctx) => {
    refineOnsiteUpsellPlacement(mod, ctx);
    refineOnsiteUpsellRules(mod.location, mod.rules, ctx);
    refineOnsiteUpsellDiscount(mod.discount, ctx);
    refineOnsiteUpsellTemplate(mod.template, ctx);
  });
